import { PatronLocationEntity } from 'components/patron-location/PatronLocation';
import { appPatronLocationSelector, getMetroAreas } from 'selectors/appSelector';
import { core } from '@tock/coretypes';
import { selectMetroArea } from 'actions/MetroAreaActions';
import { updatePatronLocationHistory } from 'actions/PatronLocationActions';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useDispatch, useSelector } from 'react-redux';
import { useIDBKeyValStore } from './useIDBKeyValStore';
import env from '@tock/shared/utils/env';

export interface UsePatronLocationState {
  currentMetroArea?: core.MetroArea;
  currentPatronLocation?: PatronLocationEntity;
  currentPatronLocationHistory: PatronLocationEntity[];
  initialized: boolean;
  metroAreaToPatronLocation(metroArea: core.MetroArea): PatronLocationEntity;
  removePatronLocationFromHistory: (key: PatronLocationHistoryKey) => void;
  resetPatronLocationHistory: () => void;
  selectPatronLocation: (location: PatronLocationEntity) => void;
  ensureMetroAreaRespectsCurrentPatronLocation: () => boolean;
  ensurePatronLocationRespectsCurrentMetroArea: (
    historyOverride?: PatronLocationEntity[]
  ) => boolean;
}

type PatronLocationHistoryKey = PatronLocationEntity['placeId'];
const PATRON_LOCATION_HISTORY_KEY = 'placeId';
const PATRON_LOCATION_HISTORY_LENGTH = 10;
const PATRON_LOCATION_IDB_KEY = 'PATRON_LOCATION_HISTORY';

const getKeyName = () => {
  const environmentName = window?.location?.hostname ? env.get(window.location.hostname) : '';
  return `${PATRON_LOCATION_IDB_KEY}${environmentName ? '-' + environmentName : ''}`;
};

/**
 * A hook that contains everything you need to read and manage patron location.
 * **Should only be used once by PatronLocationContext.**
 * Using multiple instances will lead to inaccuracies.
 * @returns {UsePatronLocationState}
 */
export function usePatronLocationState(): UsePatronLocationState {
  const dispatch = useDispatch();
  const currentMetroArea = useCurrentMetroArea();
  const patronLocation = useSelector(appPatronLocationSelector);
  const metroAreas = useSelector(getMetroAreas) as core.MetroArea[];

  // IDB is async, the initial callback happens once it has loaded its saved value
  const { saveValue } = useIDBKeyValStore<PatronLocationEntity[]>(
    getKeyName,
    [],
    ensurePatronLocationRespectsCurrentMetroArea
  );

  /**
   * Finding the appropriate metro area given a patron location
   * @returns {boolean} whether or not the metro changed.
   */
  function ensureMetroAreaRespectsCurrentPatronLocation(): boolean {
    // If no patron location item, we wait.
    if (!patronLocation.history[0]) {
      return false;
    }
    // Find the MetroArea and set it if possible.
    const metroArea = metroAreas.find((area) => area.id === patronLocation.history[0].metroAreaId);
    // If we cant find one, wait until we can.
    if (!metroArea) {
      return false;
    }

    // If the MetroArea hasnt changed, return false.
    // If we have a metro it has changed so we must update it.
    if (metroArea.id !== currentMetroArea?.id) {
      dispatch(selectMetroArea(metroArea));
      return true;
    }
    return false;
  }

  /**
   * A function that aligns the patron location history to a current metro area.
   * Used in scenarios where we care more about metro than patron location.
   * @param {PatronLocationEntity[]} history the value to sync. could come from IDB or be the current value.
   * @param {core.MetroArea?} metroArea the metro area to check against
   * @returns {boolean} whether or not the patron location value changed
   */
  function ensurePatronLocationRespectsCurrentMetroArea(historyOverride?: PatronLocationEntity[]) {
    const history = historyOverride || patronLocation.history;
    // save, but this should be re-saved again later when we have a metro.
    if (!currentMetroArea) {
      return saveHistory(history);
    }

    // if the history exists and current location metro matches current metro
    if (history.length && history[0].metroAreaId === currentMetroArea.id) {
      return saveHistory(history);
    } else {
      // we need to select metro as current patloc
      selectPatronLocation(metroAreaToPatronLocation(currentMetroArea));
      return true;
    }
  }

  // saving PatronLocation history to the browser then updating the state.
  function saveHistory(history: PatronLocationEntity[]): boolean {
    if (history !== patronLocation.history) {
      saveValue(history);
      dispatch(updatePatronLocationHistory(history));
      return true;
    } else {
      return false;
    }
  }

  // selecting a patron location pushes it to the front of history, updates store, updates IDB
  async function selectPatronLocation(location: PatronLocationEntity) {
    const updatedHistory = pushToHistory(patronLocation.history, location);
    saveHistory(updatedHistory);
  }

  // resetting the history sets to the default metro
  function resetPatronLocationHistory() {
    const updatedHistory = currentMetroArea ? [metroAreaToPatronLocation(currentMetroArea)] : [];
    saveHistory(updatedHistory);
  }

  // remove item by key (placeId), updates store, updates IDB
  function removePatronLocationFromHistory(key: PatronLocationHistoryKey) {
    const updatedHistory = cleanCopyOfHistory(patronLocation.history, key);
    saveHistory(updatedHistory);
  }

  return {
    currentMetroArea,
    currentPatronLocation: patronLocation?.history?.[0],
    currentPatronLocationHistory: patronLocation.history,
    initialized: patronLocation.initialized,
    metroAreaToPatronLocation,
    removePatronLocationFromHistory,
    resetPatronLocationHistory,
    selectPatronLocation,
    ensureMetroAreaRespectsCurrentPatronLocation,
    ensurePatronLocationRespectsCurrentMetroArea,
  };
}

/**
 * Takes a history array of PatronLocations and returns a clean copy.
 * @param {PatronLocationEntity[]} history the current history array
 * @param {PatronLocationHistoryKey} uniqueIdToRemove
 *    A unique id to remove from the array if present.
 *    Useful if you're about to push to the front of it and want to remove any duplicate.
 * @returns {PatronLocationEntity[]}
 */
function cleanCopyOfHistory(
  history: PatronLocationEntity[],
  uniqueIdToRemove?: PatronLocationHistoryKey
): PatronLocationEntity[] {
  const copy = [...history];
  if (uniqueIdToRemove) {
    const index = findHistoryIndexByKey(copy, uniqueIdToRemove);
    if (index !== -1) {
      copy.splice(index, 1);
    }
  }
  return copy;
}

/**
 *
 * @param {PatronLocationEntity[]} history history array to search
 * @param {PatronLocationHistoryKey} key the key to search for
 * @returns {number} the index of the item in the array
 */
function findHistoryIndexByKey(
  history: PatronLocationEntity[],
  key: PatronLocationHistoryKey
): number {
  return history.map((result) => result[PATRON_LOCATION_HISTORY_KEY]).indexOf(key);
}

// push a location into the history, remove duplicate, cap length at our max and return the new location array
function pushToHistory(
  history: PatronLocationEntity[],
  location: PatronLocationEntity
): PatronLocationEntity[] {
  const historyCopy = cleanCopyOfHistory(history, location[PATRON_LOCATION_HISTORY_KEY]);

  // starting the array with this item
  const updatedHistory = [location].concat(historyCopy);

  // ensuring it isnt greater than ten long
  while (updatedHistory.length > PATRON_LOCATION_HISTORY_LENGTH) {
    updatedHistory.pop();
  }

  return updatedHistory;
}

/**
 * Converts a metroArea to a PatronLocationEntity.
 * @param {core.MetroArea} metroArea the metro area to convert.
 * @returns {PatronLocationEntity}
 */
function metroAreaToPatronLocation(metroArea: core.MetroArea): PatronLocationEntity {
  const textPrimary = metroArea.state ? `${metroArea.name}, ${metroArea.state}` : metroArea.name;
  const textSecondary = metroArea.country;
  return {
    lat: metroArea.lat,
    lng: metroArea.lng,
    metroAreaId: metroArea.id,
    placeId: metroArea.googlePlacesId,
    textFull: textSecondary ? `${textPrimary}, ${textSecondary}` : textPrimary,
    textPrimary,
    textSecondary,
    type: 'city',
  };
}
